import type { FotoIntervento } from 'api'

import type { UnsentMarker } from '@/lib/sync-manager/helpers'
import type { InterventoDoc } from '@/db/local/dexie-db'
import { foldOf } from '@/features/interventi/eventi'

type Intervento = InterventoDoc & UnsentMarker
type Foto = Pick<FotoIntervento, 'riparazione_id'>

/** The repairs in the intervento's history, as the device holds it: one fold. */
export const repairIdsOf = (intervento: InterventoDoc): Set<string> =>
  new Set(
    foldOf(intervento).phases.flatMap((phase) =>
      phase.repairId ? [phase.repairId] : []
    )
  )

/**
 * Whether a photo may go to the server now. A proof photo waits until its
 * intervento has been sent and the history the server answered has its repair:
 * sent before its start, the server would refuse it. A repair the server refused
 * is gone from that history, so its photo never goes and stays on the device.
 */
export function readyToSend(
  foto: Foto,
  intervento: Intervento | undefined,
  repairIds: (intervento: Intervento) => Set<string> = repairIdsOf
) {
  if (!foto.riparazione_id) return true
  return (
    !!intervento &&
    !intervento._dirty &&
    repairIds(intervento).has(foto.riparazione_id)
  )
}

/**
 * The proof photos of repairs the server didn't record: their intervento was
 * sent and its history has no such repair. What's left on the device of a repair
 * the server refused.
 */
export function unrecordedProofPhotos<F extends Foto>(
  intervento: Intervento,
  fotos: readonly F[]
): F[] {
  if (intervento._dirty) return []
  const proofs = fotos.filter((foto) => foto.riparazione_id !== null)
  if (!proofs.length) return []
  const repairIds = repairIdsOf(intervento)
  return proofs.filter((foto) => !repairIds.has(foto.riparazione_id!))
}
